import { useMutation, useQuery } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Label from '../../../components/form/Label'
import catchError from '../../../components/global/CatchError'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyCoachToken } from '../../../hooks/useVerifyCoachToken'
import { apiClient } from '../../../services/apiClient'
import { ParentSkill, Skill } from '../../../types/skill.type'
import { StyledButton } from '../../admin/Login'
import { AdminForm } from '../../admin/users/AdminUsersForm'

type CoachSkillFormModalProps = {
    setShowEditSkillModal: (show: boolean) => void
    skillToEdit?: Skill
    setSkillToEdit: (skill?: Skill) => void
    refetchCoachSkills: () => void
}

const CoachSkillFormModal = ({
    setShowEditSkillModal,
    skillToEdit,
    setSkillToEdit,
    refetchCoachSkills,
}: CoachSkillFormModalProps) => {
    const user = useContext(UserContext)
    const [name, setName] = useState<string>(skillToEdit?.name ?? '')
    const [parentSkillId, setParentSkillId] = useState<number | undefined>(skillToEdit?.parentSkillId)
    const verifyToken = useVerifyCoachToken()

    const { data: parentSkills = [] } = useQuery<ParentSkill[]>({
        queryKey: ['parentSkills'],
        queryFn: async () => {
            verifyToken()
            const response = await apiClient.get<ParentSkill[]>('/parent-skills')
            return response.data
        },
    })

    const { t } = useTranslation('translation')

    const navigate = useNavigate()
    const lng = useLangSelected()

    const { mutate: editSkill } = useMutation({
        mutationFn: async () => {
            verifyToken()
            await apiClient.patch(`/skills/${skillToEdit?.id}`, { name, parentSkillId: parentSkillId ?? null })
            refetchCoachSkills()
            toast.success(t('coach.skill-edited'))
        },
        onError: (error: any) => {
            if (error.response?.status === 401) {
                catchError({ status: 401, message: 'Votre session a expiré' }, () => {
                    navigate(`/${lng}/login`)
                })
            } else {
                console.error('Erreur lors de la modification de la compétence:', error)
                toast.error(t('coach.skill-edit-error'))
            }
        },
    })

    const { mutate: createSkill } = useMutation({
        mutationFn: async () => {
            verifyToken()
            await apiClient.post('/skills', {
                name,
                parentSkillId: parentSkillId ?? null,
            })
        },
        onSuccess: () => {
            toast.success(t('coach.exercises.createSuccess'))
            refetchCoachSkills()
            setShowEditSkillModal(false)
        },
        onError: (error: any) => {
            if (error.response?.status === 401) {
                catchError({ status: 401, message: 'Votre session a expiré' }, () => {
                    navigate(`/${lng}/login`)
                })
            } else {
                console.error('Erreur lors de la création de la compétence:', error)
                toast.error(t('coach.skill-create-error'))
            }
        },
    })

    const initForm = () => {
        setSkillToEdit(undefined)
        setShowEditSkillModal(false)
        setName('')
    }

    return (
        <Modal
            centered
            show={true}
            onHide={() => {
                initForm()
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title id="example-custom-modal-styling-title">
                    {skillToEdit ? 'Modifier' : 'Créer'} une compétence
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <AdminForm
                    onSubmit={(e) => {
                        e.preventDefault()
                        if (!skillToEdit) createSkill()
                        else editSkill()
                        initForm()
                    }}
                >
                    <Form.Group className="mb-3">
                        <Label>Nom *</Label>
                        <Form.Control
                            type="text"
                            placeholder="Nom *"
                            onChange={(e) => setName(e.target.value)}
                            value={name ?? skillToEdit?.name ?? ''}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Compétence parente</Label>
                        <Form.Select
                            value={parentSkillId ?? ''}
                            onChange={(e) => setParentSkillId(+e.target.value || undefined)}
                        >
                            <option value="">Aucun parent</option>
                            {parentSkills.map((parentSkill) => (
                                <option key={parentSkill.id} value={parentSkill.id}>
                                    {parentSkill.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <StyledButton variant="primary" type="submit" disabled={!name}>
                        {skillToEdit ? 'Modifier' : 'Créer'}
                    </StyledButton>
                </AdminForm>
            </Modal.Body>
        </Modal>
    )
}

export default CoachSkillFormModal
